// ── GuestAccessSetup ✦ — Guest Welcome orchestration panel ──────
//
// Drops into the SetupTab right under the canonical "Share with
// your guests" QR plate. There is ONLY ONE guest QR in the whole
// app — the share plate above — and it routes guests through
// /access/:slug automatically. This panel is the quiet control
// surface for that doorway:
//
//   PRIMARY (always visible):
//     • Status toggle (Guest Welcome on / off)
//     • Welcome line (one short editorial intro, optional)
//     • Save ✦
//     • Preview guest experience ✦ (opens /access/:slug in new tab)
//
//   ADVANCED SETUP ✦ (collapsed by default — hosts touch once):
//     • Seating list (CSV upload or paste)
//     • Menu (image / PDF)
//     • Floorplan (image / PDF)
//
// Architectural rule (locked): A doorway, not a planner. No drag/
// drop seating editor, no guest CRM. The collapsed advanced setup
// IS the entirety of the operational surface.
import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Walnut · gold · ivory — mirrors SetupTab's Mode 2 palette
const INK = '#1f1409';
const INK_SOFT = '#3b2e22';
const IVORY = '#fbf6ec';
const PARCHMENT = '#f5efdf';
const GOLD = '#a4824a';
const HAIRLINE = 'rgba(31,20,9,0.14)';
const FONT_DISPLAY = "'Cormorant Garamond', 'Playfair Display', Georgia, serif";
const FONT_BODY = "'Inter', system-ui, sans-serif";

const Field = ({ label, children }) => (
  <div style={{ marginBottom: 14 }}>
    <p style={{
      margin: '0 0 6px', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.22em',
      textTransform: 'uppercase', color: GOLD,
    }}>
      {label}
    </p>
    {children}
  </div>
);

const QuietButton = ({ onClick, disabled, testid, children, primary, as = 'button', href, target }) => {
  const sharedStyle = {
    padding: '10px 16px', borderRadius: 4, fontFamily: FONT_BODY,
    fontSize: 12, fontWeight: 700, letterSpacing: '0.14em',
    textTransform: 'uppercase', cursor: disabled ? 'default' : 'pointer',
    background: primary ? INK : IVORY,
    color: primary ? IVORY : INK,
    border: primary ? 'none' : `1px solid ${HAIRLINE}`,
    opacity: disabled ? 0.55 : 1,
    textDecoration: 'none', display: 'inline-block',
  };
  if (as === 'a') {
    return (
      <a href={href} target={target} rel="noreferrer" data-testid={testid} style={sharedStyle}>
        {children}
      </a>
    );
  }
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid} style={sharedStyle}>
      {children}
    </button>
  );
};

// ── Source badge — small editorial pill that shows where each row
// came from. Hosts who see a guest appear in their list should be
// able to instantly tell whether it was the RSVP flow, their own
// CSV import, or a manual edit.
const SOURCE_BADGE = {
  invite_rsvp: { label: 'RSVP', color: '#3F7A4A' },          // soft green
  csv:         { label: 'CSV',  color: '#7A5A28' },          // walnut gold
  manual:      { label: 'Manual', color: '#5B4A2E' },        // softer ink
};

const SourcePill = ({ source }) => {
  const cfg = SOURCE_BADGE[source] || SOURCE_BADGE.csv;
  return (
    <span
      data-testid={`seating-row-source-${source}`}
      style={{
        display: 'inline-block', padding: '2px 8px',
        fontSize: 10, fontWeight: 700, letterSpacing: '0.16em',
        textTransform: 'uppercase',
        color: cfg.color, border: `1px solid ${cfg.color}33`,
        background: '#fff', borderRadius: 3,
      }}
    >{cfg.label}</span>
  );
};

// ── Inline editable seating row — one calm, mobile-first card.
// Fields stack vertically on narrow viewports (single column flex);
// on wider widths the table/seat inputs share a row. No horizontal
// scrolling, no SaaS table chrome.
const SeatingRow = ({ row, onChangeField, onDelete }) => {
  const [t, setT] = useState(row.table_name || '');
  const [s, setS] = useState(row.seat_label || '');
  const [n, setN] = useState(row.notes || '');
  const placed = !!(t && t.trim());

  // Keep local state in sync if server pushes new values
  useEffect(() => { setT(row.table_name || ''); }, [row.table_name]);
  useEffect(() => { setS(row.seat_label || ''); }, [row.seat_label]);
  useEffect(() => { setN(row.notes || ''); }, [row.notes]);

  const inputStyle = {
    padding: '8px 10px', borderRadius: 3,
    background: '#fff', border: `1px solid ${HAIRLINE}`,
    fontFamily: FONT_BODY, fontSize: 13, color: INK,
    outline: 'none', boxSizing: 'border-box', width: '100%',
  };

  return (
    <div
      data-testid={`seating-row-${row.row_id}`}
      data-placed={placed ? '1' : '0'}
      style={{
        padding: '12px 14px', marginBottom: 8,
        background: IVORY, border: `1px solid ${HAIRLINE}`, borderRadius: 4,
      }}
    >
      {/* Header — name + source + (awaiting) badge */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        <span
          data-testid={`seating-row-name-${row.row_id}`}
          style={{
            fontFamily: FONT_DISPLAY, fontSize: 17, color: INK,
            letterSpacing: '-0.01em', lineHeight: 1.2,
          }}
        >
          {row.guest_name}
        </span>
        <SourcePill source={row.source} />
        {!placed && (
          <span
            data-testid={`seating-row-awaiting-${row.row_id}`}
            style={{
              padding: '2px 8px', fontSize: 10.5, color: GOLD,
              border: `1px solid ${GOLD}33`, background: '#fff',
              borderRadius: 3, fontStyle: 'italic',
            }}
          >
            Awaiting placement ✦
          </span>
        )}
        <button
          type="button"
          onClick={() => onDelete(row.row_id)}
          data-testid={`seating-row-delete-${row.row_id}`}
          aria-label="Remove guest"
          style={{
            marginLeft: 'auto', padding: '4px 8px', cursor: 'pointer',
            background: 'transparent', border: `1px solid ${HAIRLINE}`,
            color: INK_SOFT, borderRadius: 3, fontSize: 11,
            letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 700,
          }}
        >
          Remove
        </button>
      </div>

      {/* Editable fields — Table + Seat share a row on >360px, stack below */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(120px, 100%), 1fr))',
        gap: 8,
      }}>
        <input
          type="text" value={t} placeholder="Table"
          onChange={(e) => setT(e.target.value)}
          onBlur={() => t !== (row.table_name || '') && onChangeField(row.row_id, 'table_name', t)}
          data-testid={`seating-row-table-input-${row.row_id}`}
          style={inputStyle}
        />
        <input
          type="text" value={s} placeholder="Seat"
          onChange={(e) => setS(e.target.value)}
          onBlur={() => s !== (row.seat_label || '') && onChangeField(row.row_id, 'seat_label', s)}
          data-testid={`seating-row-seat-input-${row.row_id}`}
          style={inputStyle}
        />
      </div>
      <input
        type="text" value={n} placeholder="Notes (optional)"
        onChange={(e) => setN(e.target.value)}
        onBlur={() => n !== (row.notes || '') && onChangeField(row.row_id, 'notes', n)}
        data-testid={`seating-row-notes-input-${row.row_id}`}
        style={{ ...inputStyle, marginTop: 8 }}
      />
    </div>
  );
};

const SeatingRoster = ({ rows, onChangeField, onDelete }) => {
  if (!rows || rows.length === 0) {
    return (
      <div
        data-testid="seating-roster-empty"
        style={{
          padding: '20px 16px', background: IVORY,
          border: `1px dashed ${HAIRLINE}`, borderRadius: 4,
          color: INK_SOFT, fontSize: 13.5, lineHeight: 1.6,
          fontStyle: 'italic', textAlign: 'center',
        }}
      >
        No guests have stepped into the seating list yet ✦
      </div>
    );
  }
  return (
    <div data-testid="seating-roster" style={{ marginBottom: 10 }}>
      {rows.map((r) => (
        <SeatingRow key={r.row_id} row={r} onChangeField={onChangeField} onDelete={onDelete} />
      ))}
    </div>
  );
};


export default function GuestAccessSetup({ event, onUpdate, eventUrl }) {
  // Default: ON when the field is missing (matches backend policy)
  const initialEnabled = event?.guest_access_enabled !== false;
  const [enabled, setEnabled] = useState(initialEnabled);
  const [intro, setIntro] = useState(event?.guest_access_intro || '');
  const [savingSettings, setSavingSettings] = useState(false);
  const [savedNote, setSavedNote] = useState('');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total: 0, placed: 0, awaiting: 0 });
  const [seatingBusy, setSeatingBusy] = useState(false);
  const [seatingMessage, setSeatingMessage] = useState('');
  const [pasteText, setPasteText] = useState('');
  const [menuUrl, setMenuUrl] = useState(event?.menu_asset_url || '');
  const [floorUrl, setFloorUrl] = useState(event?.floorplan_image_url || '');
  const eventId = event?.event_id;
  const slug = event?.slug;
  const previewUrl = eventUrl || (slug ? `${window.location.origin}/access/${slug}` : '');

  // Fetch the seating roster on mount and whenever the advanced panel
  // opens. While the panel is open we also poll every 12s so newly
  // accepted RSVPs surface without a page refresh.
  const loadRoster = React.useCallback(async () => {
    if (!eventId) return;
    try {
      const r = await axios.get(`${API}/events/${eventId}/guest-access/seating`);
      setRows(r.data?.rows || []);
      setSummary(r.data?.summary || { total: 0, placed: 0, awaiting: 0 });
    } catch { /* roster fetch is best-effort */ }
  }, [eventId]);

  useEffect(() => { loadRoster(); }, [loadRoster]);
  useEffect(() => {
    if (!advancedOpen) return;
    loadRoster();
    const iv = setInterval(loadRoster, 12000);
    return () => clearInterval(iv);
  }, [advancedOpen, loadRoster]);

  const saveSettings = async () => {
    if (savingSettings) return;
    setSavingSettings(true); setSavedNote('');
    try {
      await axios.post(`${API}/events/${eventId}/guest-access/settings`, {
        enabled, intro: intro.trim(),
      });
      setSavedNote('Saved ✦');
      onUpdate?.();
      setTimeout(() => setSavedNote(''), 1800);
    } finally {
      setSavingSettings(false);
    }
  };

  const uploadCsv = async (file) => {
    if (!file) return;
    setSeatingBusy(true); setSeatingMessage('');
    try {
      const fd = new FormData();
      fd.append('csv_file', file);
      const r = await axios.post(`${API}/events/${eventId}/seating/upload`, fd);
      setSeatingMessage(`${r.data?.ingested} guests placed ✦`);
      setPasteText('');
      await loadRoster();
    } catch (err) {
      setSeatingMessage(err?.response?.data?.detail || 'Could not read that list — please check the columns.');
    } finally {
      setSeatingBusy(false);
    }
  };

  const submitPaste = async () => {
    if (!pasteText.trim()) return;
    setSeatingBusy(true); setSeatingMessage('');
    try {
      const fd = new FormData();
      fd.append('paste', pasteText);
      const r = await axios.post(`${API}/events/${eventId}/seating/upload`, fd);
      setSeatingMessage(`${r.data?.ingested} guests placed ✦`);
      setPasteText('');
      await loadRoster();
    } catch (err) {
      setSeatingMessage(err?.response?.data?.detail || 'Could not read that list — please check the columns.');
    } finally {
      setSeatingBusy(false);
    }
  };

  // Inline edit — saves on blur, optimistic local update, server is
  // the source of truth. Tiny PATCH per field; the row never goes
  // back through a "save the form" step.
  const saveRowField = async (rowId, field, value) => {
    setRows((prev) => prev.map((r) => r.row_id === rowId ? { ...r, [field]: value || null, placed: field === 'table_name' ? !!(value && value.trim()) : r.placed } : r));
    try {
      await axios.patch(`${API}/events/${eventId}/guest-access/seating/${rowId}`, { [field]: value || '' });
    } catch {
      // Best effort — refresh from server to recover authoritative state
      loadRoster();
    }
    // Refresh summary counts after a table placement change
    if (field === 'table_name') loadRoster();
  };

  const deleteRow = async (rowId) => {
    setRows((prev) => prev.filter((r) => r.row_id !== rowId));
    try {
      await axios.delete(`${API}/events/${eventId}/guest-access/seating/${rowId}`);
    } finally {
      loadRoster();
    }
  };

  const uploadAsset = async (kind, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    const r = await axios.post(`${API}/events/${eventId}/guest-access/${kind}`, fd);
    if (kind === 'menu') setMenuUrl(r.data?.menu_url);
    if (kind === 'floorplan') setFloorUrl(r.data?.floorplan_url);
    onUpdate?.();
  };

  const removeAsset = async (kind) => {
    await axios.delete(`${API}/events/${eventId}/guest-access/${kind}`);
    if (kind === 'menu') setMenuUrl('');
    if (kind === 'floorplan') setFloorUrl('');
    onUpdate?.();
  };

  // ── Primary area — quiet, opinionated, one-screen ───────────
  return (
    <div
      data-testid="guest-access-setup"
      style={{
        background: PARCHMENT, padding: 'clamp(20px, 3vw, 28px)',
        border: `1px solid ${HAIRLINE}`, borderRadius: 4,
        fontFamily: FONT_BODY, color: INK,
      }}
    >
      <p style={{ margin: '0 0 14px', color: INK_SOFT, fontSize: 13.5, lineHeight: 1.65 }}>
        Guests scan the QR above to find their seat, view the menu, upload memories
        and step into tonight's experiences ✦
      </p>

      {/* Status toggle */}
      <Field label="Status">
        <label style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '10px 12px', background: IVORY,
          border: `1px solid ${HAIRLINE}`, borderRadius: 4,
          cursor: 'pointer',
        }}>
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            data-testid="guest-access-toggle"
          />
          <span style={{ fontSize: 14, color: INK }}>
            {enabled ? 'Guest Welcome is on tonight ✦' : 'Guest Welcome is off'}
          </span>
        </label>
      </Field>

      {/* Welcome line */}
      <Field label="Welcome line (optional)">
        <input
          type="text"
          value={intro}
          maxLength={240}
          onChange={(e) => setIntro(e.target.value)}
          placeholder="A short editorial line — e.g. Olivia & James' evening ✦"
          data-testid="guest-access-intro-input"
          style={{
            width: '100%', padding: '11px 14px', borderRadius: 4,
            background: IVORY, border: `1px solid ${HAIRLINE}`,
            fontFamily: FONT_BODY, fontSize: 14, color: INK, outline: 'none',
            boxSizing: 'border-box',
          }}
        />
      </Field>

      {/* Primary actions */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
        <QuietButton primary onClick={saveSettings} disabled={savingSettings} testid="guest-access-save-settings">
          {savingSettings ? 'Saving…' : 'Save ✦'}
        </QuietButton>
        {previewUrl && (
          <QuietButton as="a" href={previewUrl} target="_blank" testid="guest-access-preview-link">
            Preview guest experience ✦
          </QuietButton>
        )}
        {savedNote && (
          <span data-testid="guest-access-saved-note" style={{ fontSize: 12, color: INK_SOFT, fontStyle: 'italic' }}>
            {savedNote}
          </span>
        )}
      </div>

      {/* Quiet meta line — surfaces both placed and awaiting counts so
          the host instantly knows the work still to do. */}
      {summary.total > 0 && (
        <p
          data-testid="guest-access-seating-meta"
          style={{ margin: '4px 0 0', fontSize: 12, color: INK_SOFT, opacity: 0.75 }}
        >
          {summary.placed} seated · {summary.awaiting} awaiting placement ✦
        </p>
      )}

      {/* ── Advanced Setup ✦ — collapsed by default ─────────── */}
      <div style={{ marginTop: 26, borderTop: `1px solid ${HAIRLINE}`, paddingTop: 18 }}>
        <button
          type="button"
          onClick={() => setAdvancedOpen(v => !v)}
          data-testid="guest-access-advanced-toggle"
          aria-expanded={advancedOpen}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            width: '100%', padding: '6px 0',
            background: 'transparent', border: 'none', cursor: 'pointer',
            fontFamily: FONT_BODY, color: INK, textAlign: 'left',
          }}
        >
          <span>
            <span style={{
              display: 'block', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.22em',
              textTransform: 'uppercase', color: GOLD,
            }}>
              Advanced Setup ✦
            </span>
            <span style={{
              display: 'block', marginTop: 4, fontFamily: FONT_DISPLAY, fontSize: 18,
              color: INK, fontStyle: 'italic',
            }}>
              Seating, menu &amp; floorplan
            </span>
          </span>
          <span style={{ fontSize: 18, color: INK_SOFT, marginLeft: 12 }} aria-hidden="true">
            {advancedOpen ? '−' : '+'}
          </span>
        </button>

        {advancedOpen && (
          <div data-testid="guest-access-advanced-panel" style={{ marginTop: 18 }}>
            {/* Editable seating roster — the live record of every
                guest the system knows about. Sourced from RSVPs +
                CSV uploads + manual edits. Inline editable. */}
            <Field label={`Seating list ${summary.total ? `· ${summary.total} guests` : ''}`}>
              <SeatingRoster
                rows={rows}
                onChangeField={saveRowField}
                onDelete={deleteRow}
              />
            </Field>

            {/* CSV / paste import — secondary to the roster above */}
            <Field label="Import a list">
              <div style={{
                padding: '12px 14px', background: IVORY,
                border: `1px solid ${HAIRLINE}`, borderRadius: 4, marginBottom: 8,
                fontSize: 12.5, color: INK_SOFT, lineHeight: 1.55,
              }}>
                Columns: <b>Guest Name</b>, <b>Table</b>, <b>Seat</b> (optional), <b>Notes</b> (optional). Re-uploading replaces the previous list (RSVP'd guests auto-restore as blank rows).
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                <label
                  data-testid="guest-access-csv-label"
                  style={{
                    padding: '10px 16px', borderRadius: 4, fontSize: 12, fontWeight: 700,
                    letterSpacing: '0.14em', textTransform: 'uppercase',
                    background: IVORY, color: INK, border: `1px solid ${HAIRLINE}`,
                    cursor: 'pointer',
                  }}
                >
                  Upload CSV
                  <input
                    type="file" accept=".csv,text/csv,text/plain"
                    onChange={(e) => uploadCsv(e.target.files?.[0])}
                    style={{ display: 'none' }}
                    data-testid="guest-access-csv-input"
                  />
                </label>
              </div>
              <textarea
                rows={4}
                placeholder={"Or paste rows here — one guest per line\nGuest Name, Table, Seat, Notes"}
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                data-testid="guest-access-paste-input"
                style={{
                  width: '100%', boxSizing: 'border-box',
                  padding: '11px 14px', borderRadius: 4,
                  background: IVORY, border: `1px solid ${HAIRLINE}`,
                  fontFamily: 'ui-monospace, monospace', fontSize: 12.5,
                  color: INK, outline: 'none', resize: 'vertical', minHeight: 88,
                }}
              />
              <div style={{ marginTop: 8, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <QuietButton onClick={submitPaste} disabled={!pasteText.trim() || seatingBusy} testid="guest-access-paste-submit">
                  {seatingBusy ? 'Reading…' : 'Place these guests ✦'}
                </QuietButton>
                {seatingMessage && (
                  <span data-testid="guest-access-seating-message" style={{ fontSize: 12.5, color: INK_SOFT }}>
                    {seatingMessage}
                  </span>
                )}
              </div>
            </Field>

            {/* Menu + floorplan */}
            {[
              { key: 'menu', label: 'Menu (image or PDF)', url: menuUrl, testid: 'menu' },
              { key: 'floorplan', label: 'Floorplan (image or PDF)', url: floorUrl, testid: 'floorplan' },
            ].map((a) => (
              <Field key={a.key} label={a.label}>
                {a.url ? (
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    <a
                      href={a.url} target="_blank" rel="noreferrer"
                      data-testid={`guest-access-${a.testid}-view`}
                      style={{ fontSize: 13, color: GOLD, textDecoration: 'underline' }}
                    >
                      Preview ✦
                    </a>
                    <QuietButton onClick={() => removeAsset(a.key)} testid={`guest-access-${a.testid}-remove`}>
                      Remove
                    </QuietButton>
                  </div>
                ) : (
                  <label
                    data-testid={`guest-access-${a.testid}-label`}
                    style={{
                      display: 'inline-block',
                      padding: '10px 16px', borderRadius: 4, fontSize: 12, fontWeight: 700,
                      letterSpacing: '0.14em', textTransform: 'uppercase',
                      background: IVORY, color: INK, border: `1px solid ${HAIRLINE}`,
                      cursor: 'pointer',
                    }}
                  >
                    Upload {a.key}
                    <input
                      type="file" accept="image/*,application/pdf"
                      onChange={(e) => uploadAsset(a.key, e.target.files?.[0])}
                      style={{ display: 'none' }}
                      data-testid={`guest-access-${a.testid}-input`}
                    />
                  </label>
                )}
              </Field>
            ))}
          </div>
        )}
      </div>

      <p style={{ margin: '20px 0 0', fontSize: 11, color: INK_SOFT, lineHeight: 1.6, opacity: 0.7 }}>
        A doorway, not a planner ✦. Cards on the guest page only appear for products you've enabled.
      </p>
    </div>
  );
}
